import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/api";
import TimeseriesChart from "@/components/TimeseriesChart";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const AgencyDashboard = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAgencyOwner = user?.role === "AGENCY" && (!user?.agencyRole || user?.agencyRole === "OWNER");
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [days, setDays] = useState(() =>
    parseInt(localStorage.getItem("pref_days_agency") || "30")
  );
  useEffect(() => {
    localStorage.setItem("pref_days_agency", String(days));
  }, [days]);
  // Agency chart: aggregated across all linked organizations
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const ts = await api.agencyTimeseries(token, days);
        setSeries(ts);
      } catch (_) {
        setSeries(null);
      }
    })();
  }, [token, days]);

  // Load agency usage across linked organizations
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setUsageLoading(true);
        const u = await api.agencyUsage(token, days);
        setUsage(u);
        setOverview({
          campaigns: u?.totals?.campaigns ?? 0,
          actions: u?.totals?.actions ?? 0,
          scans: u?.period?.scans ?? 0,
          redeems: u?.period?.redeems ?? 0,
        });
      } catch (_) {
        setUsage(null);
        setOverview(null);
      } finally {
        setUsageLoading(false);
      }
    })();
  }, [token, days]);



  return (
    <div className="min-h-screen bg-muted">
      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <h1 className="text-xl font-semibold text-foreground">{t("agency.title")}</h1>
        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-2">
            {t("agency.period")}
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="border px-2 py-1 rounded text-xs"
            >
              <option value={7}>7</option>
              <option value={30}>30</option>
              <option value={90}>90</option>
            </select>
          </label>
        </div>

        <div className="bg-background p-4 rounded shadow text-xs flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">{t("agency.security.title")}</div>
            <div className="text-[11px] text-muted-foreground">
              {user?.twoFactorEnabled ? t("agency.security.enabled") : t("agency.security.disabled")}
            </div>
          </div>
          <button
            type="button"
            className="text-xs underline whitespace-nowrap"
            onClick={() => navigate("/security")}
          >
            {t("agency.security.open")}
          </button>
        </div>
        <div className="bg-background p-4 rounded shadow space-y-2">
          <h3 className="text-sm font-semibold">{t("agency.timeseries")}</h3>
          {series ? (
            <TimeseriesChart
              labels={series.labels}
              scans={series.scans}
              redeems={series.redeems}
            />
          ) : (
            <div className="text-xs text-muted-foreground">{t("common.loading")}</div>
          )}
          {series && (
            <div className="text-xs text-muted-foreground flex gap-4 pt-1">
              <span>{t("agency.scansLabel")}: {series.scans.reduce((a,b)=>a+b,0)}</span>
              <span>{t("agency.redeemsLabel")}: {series.redeems.reduce((a,b)=>a+b,0)}</span>
            </div>
          )}
        </div>
        {overview && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
            <div className="bg-background p-3 rounded shadow">
              <strong>{t("agency.cards.campaigns")}</strong>
              <div>{overview.campaigns}</div>
            </div>
            <div className="bg-background p-3 rounded shadow">
              <strong>{t("agency.cards.actions")}</strong>
              <div>{overview.actions}</div>
            </div>
            <div className="bg-background p-3 rounded shadow">
              <strong>{t("agency.cards.scans")}</strong>
              <div>{overview.scans}</div>
            </div>
            <div className="bg-background p-3 rounded shadow">
              <strong>{t("agency.cards.redeems")}</strong>
              <div>{overview.redeems}</div>
            </div>
          </div>
        )}

        <div className="bg-background p-4 rounded shadow space-y-2 text-xs">
          <h3 className="text-sm font-semibold">{t("agency.usageTitle")}</h3>
          {usageLoading ? (
            <div className="text-xs text-muted-foreground">{t("common.loading")}</div>
          ) : usage ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-muted p-3 rounded">
                <strong>{t("agency.usage.organizationsTotal")}</strong>
                <div>{usage.organizations?.total ?? 0}</div>
              </div>
              <div className="bg-muted p-3 rounded">
                <strong>{t("agency.usage.organizationsActive")}</strong>
                <div>{usage.organizations?.active ?? 0}</div>
              </div>
              <div className="bg-muted p-3 rounded">
                <strong>{t("agency.usage.emailsSent")}</strong>
                <div>{usage.period?.emailsSent ?? 0}</div>
              </div>
              <div className="bg-muted p-3 rounded">
                <strong>{t("agency.usage.emailsFailed")}</strong>
                <div>{usage.period?.emailsFailed ?? 0}</div>
              </div>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">{t("agency.usageUnavailable")}</div>
          )}
        </div>

      </main>
    </div>
  );
};

export default AgencyDashboard;
